/**
 * BlockerList Component
 * AIDLC Administrative Backend
 *
 * Displays a list of blockers with severity badges and resolution status.
 * Used in both the Overview tab and standalone views.
 */

import { Badge } from '../../shared/Badge';
import type { Blocker } from '../../../types/projects';

interface BlockerListProps {
  blockers: Blocker[];
  onResolve?: (blockerId: string) => void;
  compact?: boolean;
}

const severityVariants: Record<string, 'danger' | 'warning' | 'info' | 'neutral'> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'neutral',
};

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
};

function getAgentLabel(agentId: string | null): string {
  if (!agentId) return 'Unassigned';
  return agentId
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function BlockerList({ blockers, onResolve, compact = false }: BlockerListProps) {
  if (blockers.length === 0) {
    return (
      <div className="text-xs text-gray-400 italic py-2">No blockers.</div>
    );
  }

  return (
    <div className="space-y-2">
      {blockers.map((blocker) => (
        <div
          key={blocker.id}
          className={`border rounded-lg p-3 ${
            blocker.status === 'resolved'
              ? 'border-green-200 bg-green-50/50'
              : blocker.severity === 'critical'
              ? 'border-red-200 bg-red-50/50'
              : 'border-zinc-200 bg-white dark:bg-zinc-900 dark:border-zinc-800'
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className={`text-xs ${compact ? '' : 'text-sm'} font-medium text-gray-800 dark:text-gray-200`}>
                {blocker.description}
              </p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <Badge variant={severityVariants[blocker.severity] || 'neutral'}>
                  {blocker.severity}
                </Badge>
                <span className="text-[10px] text-gray-500">
                  Blocked: <span className="font-medium">{getAgentLabel(blocker.blockedAgentId)}</span>
                </span>
                {blocker.resolverAgentId && (
                  <span className="text-[10px] text-gray-500">
                    Resolver: <span className="font-medium">{getAgentLabel(blocker.resolverAgentId)}</span>
                  </span>
                )}
              </div>
              {blocker.crossProjectDependency && (
                <div className="mt-1.5 text-[10px] px-2 py-1 bg-purple-50 text-purple-700 rounded inline-block">
                  Cross-project: {blocker.crossProjectDependency.description}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-[10px] font-medium ${
                blocker.status === 'resolved' ? 'text-green-600' :
                blocker.status === 'in_progress' ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {statusLabels[blocker.status] || blocker.status}
              </span>

              {blocker.status !== 'resolved' && onResolve && (
                <button
                  onClick={() => onResolve(blocker.id)}
                  className="text-[10px] px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                >
                  Resolve
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
